// Telemetria leve de downloads (HLS / futuro). Cria "sessions" por
// download que acumulam timestamps em pontos-chave; ao finalizar (complete ou
// failed) persiste em localStorage rotativo (últimas N entries). Não envia
// pra backend — exposto via DownloadDiagnosticsSheet pra inspeção manual.
//
// Filosofia: telemetria NUNCA pode atrapalhar o download. Todo I/O e cálculo
// são envoltos em try/catch silenciosos. Falha no persist = perde aquela
// session, não derruba a sessão de download em si.

const STORAGE_KEY = 'ead_download_telemetry';
const MAX_ENTRIES = 50;
const SCHEMA_VERSION = 1;

// Limite de WALLCLOCK abaixo do qual interpretamos o "download" como reuse de
// cache: o plugin nativo viu todos os segmentos/arquivos já no disco (reconcile)
// e disparou batchCompleted em milissegundos sem rede real. 2s é folgado o
// suficiente pra não pegar downloads reais super-rápidos (mesmo aula pequena
// raramente termina < 5s).
//
// Usamos wallclock (não downloadOnly) de propósito: pra download de arquivo
// único, o plugin nativo só reporta progresso por contagem de item
// (0/1 → 1/1), então o primeiro batchProgress com percent>0 — que dispara o
// markFirstByte — só chega no fim. Isso faz firstByteAt ≈ completedAt, zera o
// downloadOnly e infla o ttfb pra ~wallclock. Basear cache/throughput no
// downloadOnly daria falso "reuse de cache" e throughput absurdo (5GB/s) num
// download real de 3s. Wallclock é o tempo real ponta-a-ponta.
const CACHE_REUSE_THRESHOLD_MS = 2000;

function newId () {
  // ID curto suficiente pra distinguir sessões locais (sem necessidade de UUID
  // criptográfico). timestamp + 4 chars random.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function safeReadAll () {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWriteAll (entries) {
  try {
    // Rotação FIFO: se passou do limite, descarta as mais antigas.
    const trimmed = entries.length > MAX_ENTRIES
      ? entries.slice(entries.length - MAX_ENTRIES)
      : entries;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // best-effort
  }
}

// Persiste a session no formato final (campos calculados derivados dos
// timestamps). Chamado uma vez no final de cada sessão (complete OU failed).
function persistSession (session) {
  const completedAt = session.completedAt || session.failedAt || Date.now();
  const wallclock = completedAt - session.startedAt;
  const downloadOnly = session.firstByteAt
    ? completedAt - session.firstByteAt
    : null;
  const ttfb = session.firstByteAt
    ? session.firstByteAt - session.startedAt
    : null;
  // Throughput por-entrada usa wallclock (mesma base do agregado em getStats),
  // não downloadOnly — vide comentário em CACHE_REUSE_THRESHOLD_MS. ttfb e
  // downloadOnly seguem expostos como informativos, mas pra arquivo único
  // são não-confiáveis (firstByteAt ≈ completedAt).
  const throughputBps = (session.actualBytes && wallclock > 0)
    ? Math.round(session.actualBytes / (wallclock / 1000))
    : null;

  // Heurística: completou (wallclock) em menos do que CACHE_REUSE_THRESHOLD_MS E
  // temos bytes no disco → o batch terminou porque tudo já estava baixado
  // (reconcile do plugin marcou done sem rede real). É um "no-op success" do
  // ponto de vista de tráfego — não deve entrar nos agregados (senão polui com
  // valores absurdos como 500+ MB/s).
  const reusedFromCache = Boolean(
    wallclock < CACHE_REUSE_THRESHOLD_MS &&
    session.actualBytes &&
    session.actualBytes > 0
  );

  const entry = {
    v: SCHEMA_VERSION,
    id: session.id,
    loId: session.loId,
    enrollId: session.enrollId || null,
    format: session.format,
    quality: session.quality || null,
    variantHeight: session.variantHeight || null,
    connectionType: session.connectionType || null,
    startedAt: session.startedAt,
    firstByteAt: session.firstByteAt || null,
    manifestReadyAt: session.manifestReadyAt || null,
    completedAt: session.completedAt || null,
    failedAt: session.failedAt || null,
    wallclock,
    downloadOnly,
    ttfb,
    expectedBytes: session.expectedBytes || null,
    actualBytes: session.actualBytes || null,
    throughputBps,
    segmentCount: session.segmentCount || null,
    concurrency: session.concurrency || null,
    error: session.error || null,
    cancelled: !!session.cancelled,
    reusedFromCache
  };

  try {
    const all = safeReadAll();
    all.push(entry);
    safeWriteAll(all);
  } catch {
    // best-effort
  }
}

// Inicia uma session. Retorna um objeto com métodos `mark*` pra registrar
// pontos-chave. Persiste automaticamente ao chamar markComplete/markFailed.
//
// Re-chamar markComplete ou markFailed depois do primeiro é no-op (a session
// já foi persistida).
export function start (ctx = {}) {
  const session = {
    id: newId(),
    loId: ctx.loId || null,
    enrollId: ctx.enrollId || null,
    format: ctx.format || 'unknown',
    quality: ctx.quality || null,
    connectionType: ctx.connectionType || null,
    // Concorrência efetiva do batch — permite atribuir lentidão à prioridade
    // de reprodução (1) vs download normal (6) no diagnóstico.
    concurrency: ctx.concurrency || null,
    expectedBytes: ctx.expectedBytes || null,
    startedAt: Date.now(),
    firstByteAt: null,
    manifestReadyAt: null,
    segmentCount: null,
    variantHeight: null,
    completedAt: null,
    failedAt: null,
    actualBytes: null,
    error: null,
    cancelled: false,
    _persisted: false
  };

  const persistOnce = () => {
    if (session._persisted) return;
    session._persisted = true;
    try { persistSession(session); } catch {}
  };

  return {
    markManifestReady ({ segmentCount, variantHeight } = {}) {
      session.manifestReadyAt = Date.now();
      if (typeof segmentCount === 'number') session.segmentCount = segmentCount;
      if (typeof variantHeight === 'number') session.variantHeight = variantHeight;
    },
    markFirstByte () {
      // Primeiro byte só é registrado UMA vez. Caller pode chamar várias
      // (ex: a cada batchProgress) sem se preocupar.
      if (!session.firstByteAt) session.firstByteAt = Date.now();
    },
    markCancelled () {
      session.cancelled = true;
    },
    markComplete ({ actualBytes } = {}) {
      session.completedAt = Date.now();
      if (typeof actualBytes === 'number') session.actualBytes = actualBytes;
      persistOnce();
    },
    markFailed (error) {
      session.failedAt = Date.now();
      session.error = String((error && error.message) || error || 'unknown');
      persistOnce();
    },
    // Pra debugging — retorna snapshot do estado atual da session.
    _debug () { return { ...session }; }
  };
}

// Lê todas as sessões persistidas. Ordenadas por startedAt desc (mais recente
// primeiro), pra facilitar render em UI tipo "últimos downloads".
export function getAll () {
  const entries = safeReadAll();
  return entries.slice().sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
}

// Estatísticas agregadas por formato. Útil pro painel de diagnóstico mostrar
// "Média HLS: 3m54s • 37 KB/s". Apenas sessões completed
// (sem cancel, sem erro, sem reuse de cache) entram nas médias — senão um
// re-download de cache "instantâneo" inflaria throughput artificialmente.
//
// `reusedCount` por formato é retornado separado pra que a UI possa exibir
// "(N reaproveitados do cache)" como contexto, sem misturar nos números.
export function getStats () {
  const all = getAll();
  const byFormat = {};
  all.forEach(entry => {
    const f = entry.format || 'unknown';
    if (!byFormat[f]) {
      byFormat[f] = {
        count: 0, totalWallclock: 0, totalBytes: 0,
        totalTtfb: 0, ttfbCount: 0, reusedCount: 0
      };
    }
    if (entry.cancelled || entry.error) return;
    if (entry.reusedFromCache) {
      // Conta separado, NÃO entra nas médias.
      byFormat[f].reusedCount++;
      return;
    }
    if (!entry.wallclock || !entry.actualBytes) return;
    byFormat[f].count++;
    byFormat[f].totalWallclock += entry.wallclock;
    byFormat[f].totalBytes += entry.actualBytes;
    if (typeof entry.ttfb === 'number') {
      byFormat[f].totalTtfb += entry.ttfb;
      byFormat[f].ttfbCount++;
    }
  });

  const result = {};
  Object.entries(byFormat).forEach(([format, agg]) => {
    // Se TODAS as sessões foram reuse, ainda mostra o formato (com count=0)
    // pra que a UI saiba que existiu atividade — só não há médias úteis.
    if (agg.count === 0 && agg.reusedCount === 0) return;
    result[format] = {
      count: agg.count,
      reusedCount: agg.reusedCount,
      avgWallclockMs: agg.count > 0 ? Math.round(agg.totalWallclock / agg.count) : null,
      avgBytes: agg.count > 0 ? Math.round(agg.totalBytes / agg.count) : null,
      avgThroughputBps: agg.count > 0 ? Math.round(agg.totalBytes / (agg.totalWallclock / 1000)) : null,
      avgTtfbMs: agg.ttfbCount > 0 ? Math.round(agg.totalTtfb / agg.ttfbCount) : null
    };
  });
  return result;
}

// Apaga todo o histórico. Usado pelo botão "Limpar" no diagnóstico.
export function clear () {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

// Helpers de formatação compartilhados pela UI de diagnóstico. Centralizados
// aqui pra evitar divergência de unidades entre componentes (KB/s vs MB/s).

export function formatDuration (ms) {
  if (ms == null || isNaN(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m${s.toString().padStart(2, '0')}s`;
}

export function formatThroughput (bps) {
  if (bps == null || isNaN(bps) || bps <= 0) return '—';
  const KB = 1024;
  const MB = KB * 1024;
  if (bps >= MB) return `${(bps / MB).toFixed(1)} MB/s`;
  return `${(bps / KB).toFixed(0)} KB/s`;
}
